import { ChangeDetectionStrategy, Component, ElementRef, ViewEncapsulation, booleanAttribute, computed, inject, input } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { uniqueId } from 'lodash-es';

import { statusIcons } from './assets/status-icons';
import { DsHelpGroupType } from './help-group.types';

@Component({
    selector: 'ds-help-group',
    template: `
        <div class="ds-help-text-group-header">
            <ng-content select="[slot=header]" />
        </div>
        <div class="ds-help-text-group-content">
            <ng-content />
        </div>
    `,
    styleUrls: ['./help-group.component.scss'],
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '[class.ds-help-text-group-inverse]': 'inverse()',
        '[class]': 'hostClass()',
    },
})
export class DsHelpGroup {
    readonly inverse = input(false, { transform: booleanAttribute });
    protected readonly hostClass = computed(() => `ds-help-text-group`);
}

@Component({
    selector: 'ds-help-item',
    standalone: true,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './help-group.component.scss',
    template: ` <div class="ds-help-item-icon" [innerHTML]="svg()"></div>
        <div class="ds-help-item-text">
            <ng-content select="[slot=text]" />
        </div>`,
    host: {
        '[class.ds-help-item-inverse]': 'inverse()',
        '[class]': 'hostClass()',
        'aria-atomic': 'true',
        'aria-live': 'polite',
        '[attr.id]': 'helpItemId',
    },
})
export class DsHelpItem {
    private readonly _sanitizer = inject(DomSanitizer);
    private readonly _elementRef = inject(ElementRef);

    readonly type = input<DsHelpGroupType>('success');
    readonly isRightAligned = input(false, { transform: booleanAttribute });
    readonly inverse = input(false, { transform: booleanAttribute });

    readonly svg = computed(() => {
        const rawSvg = statusIcons[this.type()];
        return rawSvg ? this._sanitizer.bypassSecurityTrustHtml(rawSvg) : '';
    });

    protected hostClass = computed(() => `ds-help-item ds-help-item-${this.type()} ${this.isRightAligned() ? 'ds-help-item-right-alignment' : ''}`);

    private _id?: string;

    get helpItemId(): string {
        if (this._id) {
            return this._id;
        }

        const element = this._elementRef.nativeElement as HTMLElement;
        let id = element.getAttribute('id');

        if (!id || !id.trim()) {
            id = uniqueId('ds-help-item-');
        }

        this._id = id;
        return id;
    }
}
